/**
 * useProfile Hook
 *
 * React hook for managing user profiles.
 * Handles loading, updating, and viewing profiles.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface Profile {
  id: string;
  username: string | null;
  display_name: string;
  avatar_url: string | null;
  bio: string | null;
  custom_status: string | null;
  pronouns: string | null;
  status: 'online' | 'offline' | 'away' | 'dnd';
  created_at: string;
  updated_at: string;
}

interface UseProfileReturn {
  // Current user's profile
  profile: Profile | null;
  loading: boolean;
  error: string | null;

  // Profile operations
  updateProfile: (updates: Partial<Profile>) => Promise<boolean>;
  uploadAvatar: (file: File) => Promise<string | null>;

  // View other profiles
  getProfile: (userId: string) => Promise<Profile | null>;

  // Channel join tracking
  joinChannel: (channelId: string) => Promise<boolean>;
  leaveChannel: (channelId: string) => Promise<boolean>;
  getOnlineUsers: (channelId: string) => Promise<Profile[]>;
}

export function useProfile(userId: string | null): UseProfileReturn {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load current user's profile
  useEffect(() => {
    if (!userId) {
      setProfile(null);
      setLoading(false);
      return;
    }

    const loadProfile = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (fetchError) {
          console.error('Error loading profile:', fetchError);
          setError('Failed to load profile');
          return;
        }

        setProfile({
          id: data.id,
          username: data.username,
          display_name: data.display_name || data.username || 'User',
          avatar_url: data.avatar_url,
          bio: data.bio,
          custom_status: data.custom_status,
          pronouns: data.pronouns,
          status: data.status || 'online',
          created_at: data.created_at,
          updated_at: data.updated_at,
        });
      } catch (err) {
        console.error('Error in loadProfile:', err);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();

    // Subscribe to profile changes
    const subscription = supabase
      .channel(`profile:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${userId}`,
        },
        (payload) => {
          const data = payload.new as any;
          setProfile(prev => prev ? {
            ...prev,
            ...data,
            display_name: data.display_name || data.username || 'User',
          } : null);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [userId]);

  // Update profile
  const updateProfile = useCallback(async (updates: Partial<Profile>): Promise<boolean> => {
    if (!userId) return false;

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (updateError) {
        console.error('Error updating profile:', updateError);
        setError(updateError.message);
        return false;
      }

      // Optimistic update
      setProfile(prev => prev ? { ...prev, ...updates } : null);
      return true;
    } catch (err) {
      console.error('Error in updateProfile:', err);
      return false;
    }
  }, [userId]);

  // Upload avatar
  const uploadAvatar = useCallback(async (file: File): Promise<string | null> => {
    if (!userId) return null;

    try {
      // Validate file
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file');
        return null;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB');
        return null;
      }

      // Generate unique filename
      const ext = file.name.split('.').pop();
      const filename = `${userId}/${Date.now()}.${ext}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filename, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) {
        console.error('Error uploading avatar:', uploadError);
        setError('Failed to upload avatar');
        return null;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filename);

      // Update profile with new avatar URL
      await updateProfile({ avatar_url: publicUrl });

      return publicUrl;
    } catch (err) {
      console.error('Error in uploadAvatar:', err);
      setError('Failed to upload avatar');
      return null;
    }
  }, [userId, updateProfile]);

  // Get another user's profile
  const getProfile = useCallback(async (targetUserId: string): Promise<Profile | null> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', targetUserId)
        .single();

      if (fetchError) {
        console.error('Error fetching profile:', fetchError);
        return null;
      }

      return {
        id: data.id,
        username: data.username,
        display_name: data.display_name || data.username || 'User',
        avatar_url: data.avatar_url,
        bio: data.bio,
        custom_status: data.custom_status,
        pronouns: data.pronouns,
        status: data.status || 'offline',
        created_at: data.created_at,
        updated_at: data.updated_at,
      };
    } catch (err) {
      console.error('Error in getProfile:', err);
      return null;
    }
  }, []);

  // Join a channel (update presence)
  const joinChannel = useCallback(async (channelId: string): Promise<boolean> => {
    if (!userId) return false;

    try {
      // Track user in channel via presence
      const channel = supabase.channel(`channel:${channelId}`);

      await channel.subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            user_id: userId,
            online_at: new Date().toISOString(),
          });
        }
      });

      return true;
    } catch (err) {
      console.error('Error joining channel:', err);
      return false;
    }
  }, [userId]);

  // Leave a channel
  const leaveChannel = useCallback(async (channelId: string): Promise<boolean> => {
    try {
      await supabase.channel(`channel:${channelId}`).unsubscribe();
      return true;
    } catch (err) {
      console.error('Error leaving channel:', err);
      return false;
    }
  }, []);

  // Get online users in a channel
  const getOnlineUsers = useCallback(async (channelId: string): Promise<Profile[]> => {
    try {
      const channel = supabase.channel(`channel:${channelId}`);
      const presenceState = channel.presenceState();

      const userIds = Object.values(presenceState)
        .flat()
        .map((p: any) => p.user_id)
        .filter(Boolean);

      if (userIds.length === 0) return [];

      const { data } = await supabase
        .from('profiles')
        .select('*')
        .in('id', userIds);

      return (data || []).map(p => ({
        id: p.id,
        username: p.username,
        display_name: p.display_name || p.username || 'User',
        avatar_url: p.avatar_url,
        bio: p.bio,
        custom_status: p.custom_status,
        pronouns: p.pronouns,
        status: 'online' as const,
        created_at: p.created_at,
        updated_at: p.updated_at,
      }));
    } catch (err) {
      console.error('Error getting online users:', err);
      return [];
    }
  }, []);

  return {
    profile,
    loading,
    error,
    updateProfile,
    uploadAvatar,
    getProfile,
    joinChannel,
    leaveChannel,
    getOnlineUsers,
  };
}
